import type { GlobalState } from '../global/types';
import type { ApiChat, ApiUser } from '../api/types';

import { getUserFullName, getMainUsername } from '../global/helpers/users';
import { downloadJson } from '../util/download';
import { getForensicDb, loadForensicDb } from '../forensic/store';
import type { ForensicPeerHistory } from '../forensic/types';
import {
  fetchCentralAudit,
  fetchCentralFullExport,
  fetchCentralUser,
  fetchCentralUserEvents,
} from './centralDb/adminFetch';
import { isCentralDbEnabled } from './centralDb/config';
import { getAccessControlData, loadAccessControl } from './accessControlStore';
import { listAuditEvents, loadInvestigationDb } from './investigation/store';
import type { AuditEvent } from './investigation/types';
import { getRasiTechData, loadRasiTechData } from './store';
import {
  enrichRegistryUserFromGlobal,
  getRegistryUser,
  loadSessionRegistry,
  type RasiAppUserRecord,
} from './sessionRegistry';
import { getUserEvents, loadUserEvents, type RasiUserEvent } from './userEventsStore';

export type PeerListItem = {
  id: string;
  title: string;
  username?: string;
  type: string;
};

export type FullUserExport = {
  exportedAt: string;
  userId: string;
  profile: {
    id: string;
    firstName?: string;
    lastName?: string;
    username?: string;
    phone?: string;
    isPremium?: boolean;
    isContact?: boolean;
    isVerified?: boolean;
    bio?: string;
  };
  registry: RasiAppUserRecord | undefined;
  ipHistory: RasiAppUserRecord['ipLogs'];
  lastIp?: string;
  loginStats: {
    firstSeenAt: number;
    lastLoginAt: number;
    loginCount: number;
    accountSlots: number[];
  };
  profileChangeHistory: ForensicPeerHistory[];
  allPeerChangeHistory: ForensicPeerHistory[];
  privateChats: PeerListItem[];
  groups: PeerListItem[];
  channels: PeerListItem[];
  contacts: PeerListItem[];
  userEvents: RasiUserEvent[];
  auditLog: AuditEvent[];
  activityLog: ReturnType<typeof getAccessControlData>['activityLog'];
  callLogs: ReturnType<typeof getRasiTechData>['callLogs'];
  forensicSummary: {
    messageCount: number;
    entityCount: number;
    sentMessagesSample: Array<{
      chatId: string;
      messageId: number;
      date: number;
      text?: string;
      isDeleted?: boolean;
    }>;
  };
  eventCounts: Record<string, number>;
};

function peerItem(chat: ApiChat): PeerListItem {
  return {
    id: chat.id,
    title: chat.title || getMainUsername(chat) || chat.id,
    username: getMainUsername(chat),
    type: chat.type,
  };
}

function contactItem(user: ApiUser): PeerListItem {
  return {
    id: user.id,
    title: getUserFullName(user) || user.id,
    username: getMainUsername(user),
    type: 'contact',
  };
}

export async function buildFullUserExport(
  userId: string,
  global: GlobalState,
): Promise<FullUserExport | undefined> {
  await loadSessionRegistry();
  await loadAccessControl();
  await loadRasiTechData();
  await loadForensicDb();
  await loadInvestigationDb();
  await loadUserEvents();

  let record = enrichRegistryUserFromGlobal(userId, global) || getRegistryUser(userId);
  if (!record && isCentralDbEnabled()) {
    record = await fetchCentralUser(userId);
  }
  if (!record) {
    const liveOnly = global.users.byId[userId];
    if (!liveOnly) return undefined;
    record = {
      userId,
      firstSeenAt: Date.now(),
      lastLoginAt: Date.now(),
      loginCount: 0,
      accountSlots: [],
      ipLogs: [],
      firstName: liveOnly.firstName,
      lastName: liveOnly.lastName,
      username: getMainUsername(liveOnly),
      phone: liveOnly.phoneNumber,
      isPremium: liveOnly.isPremium,
    };
  }

  const live = global.users.byId[userId];
  const fullInfo = global.users.fullInfoById[userId];
  const forensic = getForensicDb();
  const access = getAccessControlData();
  const data = getRasiTechData();

  const profileChangeHistory = forensic.peerHistory
    .filter((h) => h.peerId === userId)
    .sort((a, b) => b.recordedAt - a.recordedAt);

  const privateChats: PeerListItem[] = [];
  const groups: PeerListItem[] = [];
  const channels: PeerListItem[] = [];

  Object.values(global.chats.byId).forEach((chat) => {
    if (!chat) return;
    const item = peerItem(chat);
    if (chat.type === 'chatTypePrivate' || chat.type === 'chatTypeSecret') {
      privateChats.push(item);
    } else if (chat.type === 'chatTypeChannel') {
      channels.push(item);
    } else if (chat.type === 'chatTypeBasicGroup' || chat.type === 'chatTypeSuperGroup') {
      groups.push(item);
    }
  });

  const contacts = Object.values(global.users.byId)
    .filter((u) => u.isContact)
    .map(contactItem);

  let auditLog = listAuditEvents(2000).filter((e) => e.userId === userId);
  if (isCentralDbEnabled()) {
    const remote = await fetchCentralAudit(2000, userId);
    if (remote?.length) auditLog = remote;
  }

  let userEvents = getUserEvents(userId, 2000);
  if (isCentralDbEnabled()) {
    const remoteEvents = await fetchCentralUserEvents(userId, 2000);
    if (remoteEvents?.length) {
      const merged = new Map<string, RasiUserEvent>();
      remoteEvents.forEach((e) => merged.set(e.id, e));
      userEvents.forEach((e) => merged.set(e.id, e));
      userEvents = [...merged.values()].sort((a, b) => b.timestamp - a.timestamp);
    }
  }
  const eventCounts: Record<string, number> = {};
  userEvents.forEach((e) => {
    eventCounts[e.type] = (eventCounts[e.type] || 0) + 1;
  });

  const sentMessages = Object.values(forensic.messages)
    .filter((m) => m.senderId === userId)
    .sort((a, b) => b.date - a.date)
    .slice(0, 300)
    .map((m) => ({
      chatId: m.chatId,
      messageId: m.messageId,
      date: m.date,
      text: m.text?.slice(0, 200),
      isDeleted: m.isDeleted,
    }));

  let centralFullExport: object | undefined;
  if (isCentralDbEnabled()) {
    centralFullExport = await fetchCentralFullExport(userId);
  }

  return {
    exportedAt: new Date().toISOString(),
    userId,
    centralFullExportSnapshot: centralFullExport,
    profile: {
      id: userId,
      firstName: live?.firstName ?? record.firstName,
      lastName: live?.lastName ?? record.lastName,
      username: getMainUsername(live) ?? record.username,
      phone: live?.phoneNumber ?? record.phone,
      isPremium: live?.isPremium ?? record.isPremium,
      isContact: live?.isContact,
      isVerified: live?.isVerified ?? record.isVerified,
      bio: fullInfo && 'about' in fullInfo ? fullInfo.about : undefined,
    },
    registry: record,
    ipHistory: record.ipLogs || [],
    lastIp: record.lastIp,
    loginStats: {
      firstSeenAt: record.firstSeenAt,
      lastLoginAt: record.lastLoginAt,
      loginCount: record.loginCount,
      accountSlots: record.accountSlots || [],
    },
    profileChangeHistory,
    allPeerChangeHistory: forensic.peerHistory.slice(-500).reverse(),
    privateChats: privateChats.sort((a, b) => a.title.localeCompare(b.title)),
    groups: groups.sort((a, b) => a.title.localeCompare(b.title)),
    channels: channels.sort((a, b) => a.title.localeCompare(b.title)),
    contacts: contacts.sort((a, b) => a.title.localeCompare(b.title)),
    userEvents,
    auditLog,
    activityLog: access.activityLog.filter((e) => e.userId === userId).slice(-300),
    callLogs: data.callLogs.filter((l) => l.ownerUserId === userId || l.peerId === userId),
    forensicSummary: {
      messageCount: Object.keys(forensic.messages).length,
      entityCount: Object.keys(forensic.entities).length,
      sentMessagesSample: sentMessages,
    },
    eventCounts,
  };
}

export async function downloadFullUserExport(userId: string, global: GlobalState): Promise<boolean> {
  const payload = await buildFullUserExport(userId, global);
  if (!payload) return false;
  downloadJson(payload, `nusantara_user_${userId}_${Date.now()}.json`);
  return true;
}
